import React, { useState } from 'react';
import './StyledInput.css';
import ParagraphText from './ParagraphText';
import NumberInput from './NumberInput';
import DateInput from './DateInput';
import { formatDate } from './tenantClock';
import { formatNumber } from './numberFormat';
import { useScreenAccess } from './PermissionsContext';

const labelStyle = { fontSize: 12, color: '#5a6b85', textTransform: 'uppercase', letterSpacing: 0.3 };
const inputStyle = { width: '100%', fontSize: 15, fontFamily: 'inherit', boxSizing: 'border-box' };

// Click-to-edit-and-auto-save for a single field on a record-detail page's
// static-data panel: renders as plain text, becomes its real input on
// click, and saves on blur/Enter with no separate Save button — Escape
// reverts to the last-saved value without saving. `onSave` must return a
// Promise; a rejection keeps editing open with the typed value intact and
// shows the rejection's message inline, since there's no modal/toast for
// the person to lose their place in mid-edit. Plain text only, never
// editable, on a screen the staff member can't update (issue #252).
// readOnly shows the value without the click-to-edit, e.g. Added By.
// formatInput tidies a text value as it's typed, e.g. spacing an ABN.
function InlineEditField({ label, labelExtra, value, onSave, type = 'text', options, placeholder, formatDisplay, readOnly = false, formatInput }) {
  const { canUpdate: canUpdateScreen } = useScreenAccess();
  const canUpdate = canUpdateScreen && !readOnly;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const startEdit = () => {
    if (saving || !canUpdate) return;
    setDraft(formatInput && value ? formatInput(value) : (value ?? ''));
    setError('');
    setEditing(true);
  };

  const cancel = () => {
    setDraft(value ?? '');
    setError('');
    setEditing(false);
  };

  // `next` is the value a DateInput settled on at blur, which can be ahead
  // of `draft` when a looser entry was tidied on the way out.
  const commit = async (next = draft) => {
    const settled = next ?? '';
    if (settled === (value ?? '')) { setEditing(false); return; }
    setSaving(true);
    setError('');
    try {
      await onSave(settled === '' ? null : settled);
      setEditing(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save');
    }
    setSaving(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && type !== 'textarea') { e.preventDefault(); commit(); }
    if (e.key === 'Escape') cancel();
  };

  if (!editing) {
    let display;
    if (formatDisplay) display = formatDisplay(value);
    else if (type === 'textarea' && value) display = <ParagraphText text={value} />;
    else if (type === 'number') display = formatNumber(value, { maxDecimals: 4 }) || '—';
    else if (type === 'date') display = formatDate(value) || '—';
    else display = value || value === 0 ? value : '—';
    return (
      <div style={{ marginBottom: 10 }}>
        <div style={labelStyle}>
          {label}
          {labelExtra && <> {labelExtra}</>}
        </div>
        <div
          className={canUpdate ? 'inline-edit-field' : undefined}
          onClick={startEdit}
          title={canUpdate ? 'Click to edit' : undefined}
          style={{ fontSize: 15, color: '#222', wordBreak: 'break-word', cursor: canUpdate ? 'pointer' : 'default', padding: '2px 4px', margin: '-3px -5px', borderRadius: 4 }}
        >
          {display}
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={labelStyle}>{label}</div>
      {type === 'select' ? (
        <select className="styled-input" autoFocus value={draft ?? ''} disabled={saving} onChange={(e) => setDraft(e.target.value)} onBlur={() => commit()} onKeyDown={handleKeyDown} style={inputStyle}>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : type === 'textarea' ? (
        <textarea className="styled-input" autoFocus value={draft ?? ''} disabled={saving} onChange={(e) => setDraft(e.target.value)} onBlur={() => commit()} onKeyDown={handleKeyDown} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
      ) : type === 'date' ? (
        <DateInput autoFocus value={draft ?? ''} disabled={saving} onChange={(e) => setDraft(e.target.value)} onBlur={(e) => commit(e.target.value)} onKeyDown={handleKeyDown} style={{ width: 180 }} />
      ) : type === 'number' ? (
        // Typed in the company Number Format, saved in the plain 1234.56 form.
        <NumberInput autoFocus value={draft ?? ''} disabled={saving} placeholder={placeholder} onChange={(e) => setDraft(e.target.value)} onBlur={() => commit()} onKeyDown={handleKeyDown} style={inputStyle} />
      ) : (
        <input className="styled-input" autoFocus type={type === 'url' ? 'text' : type} value={draft ?? ''} disabled={saving} placeholder={placeholder} onChange={(e) => setDraft(formatInput ? formatInput(e.target.value) : e.target.value)} onBlur={() => commit()} onKeyDown={handleKeyDown} style={inputStyle} />
      )}
      {error && <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 2 }}>{error}</div>}
    </div>
  );
}

export default InlineEditField;
